import type {
  ActivityDependencyCriterion,
  ActivityDependencySummaryDTO,
} from 'src/types/dto';

import { Typography } from '@mui/material';

import { TagsList } from 'src/components/tags-list';

type DependencyTranslate = (key: string, options?: Record<string, unknown>) => string;

function formatCriterionLabel(
  criterion: ActivityDependencyCriterion,
  t: DependencyTranslate
): string {
  const bound = t(`form.details.dependencies.boundType.${criterion.type.toLowerCase()}`);
  const unit = t(
    `form.details.dependencies.durationType.${criterion.durationType.toLowerCase()}`,
    { count: criterion.duration }
  );
  const direction = t(
    `form.details.dependencies.direction.${criterion.direction.toLowerCase()}`
  );
  return t('grid.columns.dependencyCriterion', {
    bound,
    duration: criterion.duration,
    unit,
    direction,
  });
}

export function formatDependencyTagLabel(
  dep: ActivityDependencySummaryDTO,
  noSiteLabel: string,
  t: DependencyTranslate
): string {
  const base = dep.site?.name ? `${dep.site.name} – ${dep.name}` : dep.name;
  const criteria = dep.criteria ?? [];
  if (criteria.length === 0) {
    return base;
  }
  const criteriaLabel = criteria.map((c) => formatCriterionLabel(c, t)).join(', ');
  return `${base} (${criteriaLabel})`;
}

type Props = {
  dependencies: ActivityDependencySummaryDTO[];
  containerId: string;
  t: DependencyTranslate;
  emptyLabel: string;
};

export function ActivityDependencyTags({
  dependencies,
  containerId,
  t,
  emptyLabel,
}: Props) {
  if (dependencies.length === 0) {
    return <Typography variant="body2">{emptyLabel}</Typography>;
  }

  const noSite = t('form.details.dependencies.noSite');

  return (
    <TagsList
      containerIdentifier={containerId}
      tags={dependencies.map((dep) => ({
        id: dep.id,
        label: formatDependencyTagLabel(dep, noSite, t),
        color: 'default',
      }))}
      color="default"
      showTooltip
    />
  );
}
